// src/routes/index.tsx
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import ProtectedRoute from '../pages/auth/ProtectedRoute';
import { useRolePermissions } from '../hooks/useRolePermissions';

// ─── Eagerly loaded (critical path — always needed) ──────────────────────────
import MainLayout from '../components/layout/MainLayout';
import Login from '../pages/auth/Login';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';

// ─── Lazily loaded (split into separate chunks) ──────────────────────────────
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const ClientList = lazy(() => import('../pages/clients/ClientList'));
const MaintenanceList = lazy(() => import('../pages/maintenance/MaintenanceList'));
const ComplianceList = lazy(() => import('../pages/compliance/ComplianceList'));
const ComplianceCheckList = lazy(() => import('../pages/compliance/Checklist'));
const MaintenanceCompliance = lazy(() => import('../pages/compliance/Maintenance'));
const UserRoles = lazy(() => import('../pages/users/UserRoles'));
const UserManagement = lazy(() => import('../pages/users/UserManagement'));
const Departments = lazy(() => import('../pages/users/Departments'));
const Settings = lazy(() => import('../pages/settings'));
const SystemSettings = lazy(() => import('../pages/settings/SystemSettings'));
const Archive = lazy(() => import('../pages/archive/Archive'));
const SharedArchiveView = lazy(() => import('../pages/archive/SharedArchiveView'));
const UnderDevelopmentPage = lazy(() => import('../pages/common/UnderDevelopmentPage'));
const InventoryList = lazy(() => import('../pages/inventory/InventoryList'));
const ProcurementPage = lazy(() => import('../pages/procurement/ProcurementPage'));

// ─── Loading fallback ─────────────────────────────────────────────────────────
const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
      padding: '3rem',
      color: '#666',
      fontSize: '0.875rem',
    }}
  >
    Loading…
  </div>
);

// ─── Super-admin guard ────────────────────────────────────────────────────────
const SuperAdminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useRolePermissions();
  return isSuperAdmin ? <>{children}</> : <Navigate to="/" replace />;
};

// ─── Auth-aware root redirect ─────────────────────────────────────────────────
const IndexRedirect: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  return isAuthenticated
    ? <Navigate to="/dashboard" replace />
    : <Navigate to="/login" replace />;
};

const AppRoutes: React.FC = () => (
  <BrowserRouter>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<IndexRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Shared archive view — accessible without full auth layout */}
        <Route
          path="/archive/shared/:archiveId"
          element={
            <ProtectedRoute>
              <SharedArchiveView />
            </ProtectedRoute>
          }
        />

        {/* Protected routes inside main layout */}
        <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/clients" element={<ClientList />} />
          <Route path="/maintenance" element={<MaintenanceList />} />
          <Route path="/compliance" element={<ComplianceList />} />
          <Route path="/compliance-checklist" element={<ComplianceCheckList />} />
          <Route path="/compliance-maintenance" element={<MaintenanceCompliance />} />
          <Route path="/user-roles" element={<UserRoles />} />
          <Route path="/user-management" element={<UserManagement />} />
          <Route path="/departments" element={<Departments />} />
          <Route path="/settings" element={<Settings />} />
          <Route
            path="/settings/system"
            element={
              <SuperAdminOnly>
                <SystemSettings />
              </SuperAdminOnly>
            }
          />
          <Route path="/archive" element={<Archive />} />
          <Route path="/notifications" element={<UnderDevelopmentPage />} />
          <Route path="/procurement" element={<ProcurementPage />} />
          <Route path="/inventory" element={<InventoryList />} />
          <Route path="/disposal" element={<UnderDevelopmentPage />} />
          <Route path="/reports" element={<UnderDevelopmentPage />} />
          <Route
            path="*"
            element={
              <UnderDevelopmentPage
                title="Page Not Found"
                description="The page you're looking for doesn't exist or is currently under development."
              />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  </BrowserRouter>
);

export default AppRoutes;
